import {
  Chip,
  ChipRemove,
  Combobox,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useComboboxAnchor,
} from "@aircall/ds";
import { type ReactNode, useState } from "react";

import {
  MultiSelectTagChip,
  MultiSelectTagItem,
} from "@/components/SectionTags/MultiSelectTagItem";
import { TagBeautified } from "@/components/TagBeautified/TagBeautified";
import { MAX_LENGTH } from "@/components/TagList/TagsListItem.constants";
import { trimWithEllipsis } from "@/helpers/string/string.helpers";
import { TagOption } from "@/hooks/useTags/useTags.decl";

import { PlaygroundSectionLayout } from "../PlaygroundSectionLayout/PlaygroundSectionLayout";
import { PlaygroundSubSectionLayout } from "../PlaygroundSubSectionLayout/PlaygroundSubSectionLayout";
import { PlaygroundSubSectionTitle } from "../PlaygroundSubSectionTitle/PlaygroundSubSectionTitle";

// The tag name from the bug report (25 chars, well over MAX_LENGTH=18).
const LONG_NAME = "SDR - Demo Scheduled Call";
const SHORT_NAME = "Support";

const COLOR = "#307FE2";

const LONG_OPTION: TagOption = {
  value: "long",
  label: LONG_NAME,
  color: COLOR,
};
const SHORT_OPTION: TagOption = {
  value: "short",
  label: SHORT_NAME,
  color: COLOR,
};

// Dropdown options mirroring the long company-tag names from the bug report.
const DROPDOWN_OPTIONS: TagOption[] = [
  LONG_OPTION,
  { value: "pos", label: "Positive Champion Call Connected", color: COLOR },
  { value: "neg", label: "Negative Champion Call Connected", color: COLOR },
  { value: "gate", label: "SDR - Gatekeeper Call Connected", color: COLOR },
  { value: "busy", label: "SDR - Connected Busy", color: COLOR },
  SHORT_OPTION,
];

/**
 * Boxes children to roughly the call-detail left-panel width so overflow
 * actually reproduces — in a wide container a long chip has room and never
 * clips. The border makes the panel edge visible.
 */
function PanelWidth({ children }: { children: ReactNode }) {
  return (
    <div className="w-[280px] rounded-md border border-border p-2">
      {children}
    </div>
  );
}

/**
 * Faithful copy of `TagsListItem`'s inner markup (display mode) — minus the
 * router/store/Apollo hooks — so we can compare the trimmed display path
 * against the untrimmed edit path side by side.
 */
function DisplayTag({ name }: { name: string }) {
  const tag = (
    <TagBeautified color={COLOR}>
      <span className="text-tiny font-medium">
        {trimWithEllipsis(name, MAX_LENGTH)}
      </span>
      <ChipRemove />
    </TagBeautified>
  );

  if (name.length > MAX_LENGTH) {
    return (
      <Tooltip>
        <TooltipTrigger render={<span />}>{tag}</TooltipTrigger>
        <TooltipContent side="top" align="center">
          {name}
        </TooltipContent>
      </Tooltip>
    );
  }

  return tag;
}

/**
 * Display path (TagsListItem). Trims at MAX_LENGTH and shows a tooltip — this
 * is the render in image #4 and behaves correctly.
 */
function DisplayCase() {
  return (
    <PanelWidth>
      <div className="flex flex-wrap gap-1">
        <DisplayTag name={LONG_NAME} />
        <DisplayTag name={SHORT_NAME} />
      </div>
    </PanelWidth>
  );
}

/**
 * Edit path (the inline tag editor's selected chips). Renders the real
 * `MultiSelectTagChip` inside a Combobox/ComboboxChips, mirroring `TagSelector`.
 * `MultiSelectTagChip` now trims the label (+ tooltip) like the display path,
 * so the long tag truncates here instead of clipping — the chip used to print
 * `option.label` raw and clip it (the original bug).
 */
function EditCase() {
  // Stateful so the chip's native × remove actually re-renders — lets us verify
  // that wrapping the chip in a Tooltip didn't sever Base UI's chip removal.
  const [value, setValue] = useState<TagOption[]>([LONG_OPTION, SHORT_OPTION]);

  return (
    <PanelWidth>
      <Combobox<TagOption, true>
        multiple
        open={false}
        inputValue=""
        items={[]}
        value={value}
        onValueChange={setValue}
        onInputValueChange={() => {}}
        onOpenChange={() => {}}
        isItemEqualToValue={(a, b) => a.value === b.value}
        filter={null}
      >
        <ComboboxChips data-test="playground-tag-chips">
          {value.map((option) => (
            <MultiSelectTagChip key={option.value} option={option} />
          ))}
          <ComboboxChipsInput />
        </ComboboxChips>
      </Combobox>
    </PanelWidth>
  );
}

/**
 * Dropdown rows (MultiSelectTagItem). Renders the Combobox open with items so
 * the real ComboboxContent > ComboboxList > ComboboxItem > MultiSelectTagItem
 * dropdown shows. The rows now trim long labels (+ tooltip) instead of clipping
 * the text mid-word.
 */
function DropdownCase() {
  const anchorRef = useComboboxAnchor();

  return (
    <PanelWidth>
      <Combobox<TagOption, true>
        multiple
        open
        inputValue=""
        items={DROPDOWN_OPTIONS}
        value={[]}
        onValueChange={() => {}}
        onInputValueChange={() => {}}
        onOpenChange={() => {}}
        isItemEqualToValue={(a, b) => a.value === b.value}
        filter={null}
      >
        <ComboboxChips ref={anchorRef}>
          <ComboboxChipsInput />
        </ComboboxChips>
        <ComboboxContent anchor={anchorRef}>
          <ComboboxList>
            {DROPDOWN_OPTIONS.map((option) => (
              <ComboboxItem key={option.value} value={option}>
                <MultiSelectTagItem option={option} />
              </ComboboxItem>
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </PanelWidth>
  );
}

/**
 * Raw chip with no truncation at all, for reference — shows the unguarded
 * overflow behaviour of the underlying DS `Chip`.
 */
function RawChipCase() {
  return (
    <PanelWidth>
      <div className="flex flex-wrap gap-1">
        <Chip style={{ backgroundColor: COLOR, color: "#fff" }}>
          <span className="text-tiny font-medium">{LONG_NAME}</span>
        </Chip>
      </div>
    </PanelWidth>
  );
}

export function PlaygroundTagDisplay() {
  return (
    <PlaygroundSectionLayout>
      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>
          Edit mode (MultiSelectTagChip — trims + tooltip)
        </PlaygroundSubSectionTitle>
        <EditCase />
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>
          Dropdown rows (MultiSelectTagItem — trims + tooltip)
        </PlaygroundSubSectionTitle>
        <DropdownCase />
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>
          Display mode — reference (TagsListItem, trims + tooltip)
        </PlaygroundSubSectionTitle>
        <DisplayCase />
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>
          Raw DS Chip — no truncation
        </PlaygroundSubSectionTitle>
        <RawChipCase />
      </PlaygroundSubSectionLayout>
    </PlaygroundSectionLayout>
  );
}
